import React from 'react';
import {
  AngryFaceIcon,
  FrownFaceIcon,
  MehFaceIcon,
  SmileFaceIcon,
  GrinFaceIcon,
} from '../icons';

export const LIKERT_MOODS = [
  { value: 1, label: 'Muy mala', icon: AngryFaceIcon, accent: 'text-rose-500', selectedBg: 'bg-rose-50', selectedBorder: 'border-rose-400', selectedText: 'text-rose-700', ringColor: 'ring-rose-200' },
  { value: 2, label: 'Necesita mejorar', icon: FrownFaceIcon, accent: 'text-amber-500', selectedBg: 'bg-amber-50', selectedBorder: 'border-amber-400', selectedText: 'text-amber-700', ringColor: 'ring-amber-200' },
  { value: 3, label: 'Neutral', icon: MehFaceIcon, accent: 'text-slate-500', selectedBg: 'bg-slate-50', selectedBorder: 'border-slate-400', selectedText: 'text-slate-700', ringColor: 'ring-slate-200' },
  { value: 4, label: 'Buena', icon: SmileFaceIcon, accent: 'text-emerald-500', selectedBg: 'bg-emerald-50', selectedBorder: 'border-emerald-400', selectedText: 'text-emerald-700', ringColor: 'ring-emerald-200' },
  { value: 5, label: 'Excelente', icon: GrinFaceIcon, accent: 'text-sky-500', selectedBg: 'bg-sky-50', selectedBorder: 'border-sky-400', selectedText: 'text-sky-700', ringColor: 'ring-sky-200' },
];

export const LikertScale = ({ question, value, onChange, error }) => {
  const selectedValue = parseInt(value, 10);
  return (
    <div className={`space-y-4 rounded-2xl border ${error ? 'border-rose-300 bg-rose-50/40' : 'border-slate-200/50 bg-white/95'} p-4 shadow-card md:p-5`}>
      <label className="block text-base font-semibold text-slate-900">{question.text}</label>
      {question.interpretation && (
        <p className="text-sm text-slate-500">{question.interpretation}</p>
      )}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {LIKERT_MOODS.map((mood) => {
          const isSelected = selectedValue === mood.value;
          const MoodIcon = mood.icon;
          return (
            <button
              key={mood.value}
              type="button"
              onClick={() => onChange(question.id, mood.value.toString())}
              className={`flex flex-col items-center gap-2.5 rounded-2xl border bg-white px-2 py-3.5 text-center transition-all duration-200 ${
                isSelected
                  ? `${mood.selectedBorder} ${mood.selectedBg} ${mood.selectedText} shadow-card scale-[1.03] ring-2 ${mood.ringColor}`
                  : 'border-slate-200 text-slate-500 hover:border-slate-300 hover:shadow-sm'
              }`}
            >
              <span
                className={`flex h-12 w-12 items-center justify-center rounded-full border transition-all duration-200 ${
                  isSelected ? `${mood.selectedBorder} ${mood.selectedBg} ${mood.selectedText}` : `border-slate-200 ${mood.accent}`
                }`}
              >
                <MoodIcon className="h-6 w-6" />
              </span>
              <span className="text-[0.68rem] font-semibold uppercase leading-tight">{mood.label}</span>
            </button>
          );
        })}
      </div>
      {error && <p className="text-rose-500 text-sm">{error}</p>}
    </div>
  );
};

export const BinaryChoice = ({ question, value, onChange, error }) => {
  const options = [
    { label: question.positive, value: question.positive, icon: '✓', selectedBg: 'bg-emerald-50', selectedBorder: 'border-emerald-400', selectedText: 'text-emerald-700', ringColor: 'ring-emerald-200' },
    { label: question.negative, value: question.negative, icon: '✗', selectedBg: 'bg-rose-50', selectedBorder: 'border-rose-400', selectedText: 'text-rose-700', ringColor: 'ring-rose-200' },
  ];
  return (
    <div className={`space-y-4 rounded-2xl border ${error ? 'border-rose-300 bg-rose-50/40' : 'border-slate-200/50 bg-white/95'} p-4 shadow-card md:p-5`}>
      <label className="block text-base font-semibold text-slate-900">{question.text}</label>
      <div className="grid grid-cols-2 gap-3">
        {options.map((option) => {
          const isSelected = value === option.value;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onChange(question.id, option.value)}
              className={`flex items-center justify-center gap-2.5 rounded-2xl border px-4 py-3.5 text-sm font-semibold transition-all duration-200 ${
                isSelected
                  ? `${option.selectedBorder} ${option.selectedBg} ${option.selectedText} shadow-card ring-2 ${option.ringColor}`
                  : 'border-slate-200 bg-slate-50/80 text-slate-600 hover:border-slate-300 hover:shadow-sm'
              }`}
            >
              <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${
                isSelected ? `${option.selectedBg} ${option.selectedText} border ${option.selectedBorder}` : 'bg-slate-100 text-slate-400 border border-slate-200'
              }`}>
                {option.icon}
              </span>
              {option.label}
            </button>
          );
        })}
      </div>
      {error && <p className="text-rose-500 text-sm">{error}</p>}
    </div>
  );
};

export const OpenText = ({ question, value, onChange, error }) => {
  return (
    <div className={`space-y-3 rounded-2xl border ${error ? 'border-rose-300 bg-rose-50/40' : 'border-slate-200/50 bg-white/95'} p-4 shadow-card md:p-5`}>
      <label htmlFor={question.id} className="block text-base font-semibold text-slate-900">{question.text}</label>
      <textarea
        id={question.id}
        name={question.id}
        rows={3}
        value={value || ''}
        onChange={e => onChange(question.id, e.target.value)}
        className="block w-full rounded-xl border border-slate-200 bg-slate-50/80 px-4 py-3 text-sm text-slate-800 placeholder:italic placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#004AAD]/25 focus:border-[#004AAD]/40 transition-all duration-200"
        placeholder="Escribe tu respuesta aquí..."
      />
      {error && <p className="text-rose-500 text-sm">{error}</p>}
    </div>
  );
};

export const SelectChoice = ({ question, value, onChange, error }) => {
  return (
    <div className={`space-y-3 rounded-2xl border ${error ? 'border-rose-300 bg-rose-50/40' : 'border-slate-200/50 bg-white/95'} p-4 shadow-card md:p-5`}>
      <label htmlFor={question.id} className="block text-base font-semibold text-slate-900">{question.text}</label>
      <select
        id={question.id}
        name={question.id}
        value={value || ''}
        onChange={(e) => onChange(question.id, e.target.value)}
        className="block w-full rounded-xl border border-slate-200 bg-slate-50/80 px-4 py-3 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#004AAD]/25 focus:border-[#004AAD]/40 transition-all duration-200"
      >
        <option value="">Selecciona una opción</option>
        {(question.options || []).map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      {error && <p className="text-rose-500 text-sm">{error}</p>}
    </div>
  );
};
